import axios from 'axios'
import useJwt from '@/auth/jwt/useJwt'

const equipmentMap = {
  BRN: 'borne',
  STN: 'station',
  ARC: 'arche',
  BTI: 'borneTouristique',
  BTE: 'borneTouristiqueExterieure',
  BTM: 'borneTouristiqueMurale',
  BOR: 'borneOrnithologie',
  BRV: 'borneRechargeVelo',
  LMP: 'lampadaire',
  RMQ: 'remorque',
  KRS: 'kairos',
  BVC: 'borneVC',
  BVR: 'borneVR',
}

// list of clients that map to "Gaspésie"
const gaspeClients = [
  'Tourisme Gaspésie',
  'Développement Économique Matanie',
  'MRC de la Haute-Gaspésie',
  'Destination Gaspé',
  'Ville de Percé',
  'Ville de New Richmond',
  'Carleton-sur-Mer',
  'Grande-Vallée',
  'Chandler',
]

// list of clients that map to "Outaouais"
const outaouaisClients = [
  'Outaouais',
  'MRC de Pontiac',
]

// list of clients that map to "Laurentides"
const laurentidesClients = [
  'Tourisme Laurentides',
  'Basses-Laurentides',
  'Saint-Sauveur',
  'Les Hautes-Laurentides',
  'Tourisme Mont-Tremblant',
]

const state = {
  equipments: [],
}

const getters = {
  // expose the const equipmentMap
  equipmentMap: () => equipmentMap,
  /**
   * Returns 'Gaspésie' for any of your gaspe‐clients,
   * 'Les Escoumins' for that one, or the raw clientName otherwise.
   */
  getClientGroup: () => clientName => {
    if (clientName === 'Les Escoumins') return 'Les Escoumins'
    if (clientName === 'Îles-de-la-Madeleine') return 'Îles-de-la-Madeleine'
    if (gaspeClients.includes(clientName)) return 'Gaspésie'
    if (outaouaisClients.includes(clientName)) return 'Outaouais'
    if (laurentidesClients.includes(clientName)) return 'Laurentides'
    return clientName
  },

  // Extract major version (first number)
  getMajorVersion: () => version => {
    const v = String(version || '').trim()
    const m = v.match(/^(\d+)/)
    return m ? m[1] : ''
  },

  // Map version -> Superset dashboard ID (BTE)
  getBteSupersetDashboardIdByVersion: () => version => {
    // Accept versions like "3" or "3.1". Prefer a specific minor env var
    const ver = String(version || '').trim()
    const [major, minor] = ver.split('.')
    if (minor) {
      const key = `VUE_APP_SUPERSET_DASHBOARD_ID_BTE_V${major}_${minor}`
      if (process.env[key]) return process.env[key]
    }
    if (major === '3') return process.env.VUE_APP_SUPERSET_DASHBOARD_ID_BTE_V3
    if (major === '2') return process.env.VUE_APP_SUPERSET_DASHBOARD_ID_BTE_V2
    if (major === '1') return process.env.VUE_APP_SUPERSET_DASHBOARD_ID_BTE_V1
    return process.env.VUE_APP_SUPERSET_DASHBOARD_ID_BTE_V3
      || process.env.VUE_APP_SUPERSET_DASHBOARD_ID_BTE_V2
      || process.env.VUE_APP_SUPERSET_DASHBOARD_ID_BTE_V1
  },

  // Map BTM -> single Superset dashboard ID
  getBtmSupersetDashboardId: () => process.env.VUE_APP_SUPERSET_DASHBOARD_ID_BTM || process.env.VUE_APP_SUPERSET_DASHBOARD_ID_BTE_V3,

  // Map version -> Superset dashboard ID (BTI)
  getBtiSupersetDashboardIdByVersion: () => version => {
    // Accept versions like "3" or "3.1". Prefer a specific minor env var
    const ver = String(version || '').trim()
    const [major, minor] = ver.split('.')
    if (minor) {
      const key = `VUE_APP_SUPERSET_DASHBOARD_ID_BTI_V${major}_${minor}`
      if (process.env[key]) return process.env[key]
    }
    if (major === '3') return process.env.VUE_APP_SUPERSET_DASHBOARD_ID_BTE_V3
    if (major === '2') return process.env.VUE_APP_SUPERSET_DASHBOARD_ID_BTI
    if (major === '1') return process.env.VUE_APP_SUPERSET_DASHBOARD_ID_BTI
    return process.env.VUE_APP_SUPERSET_DASHBOARD_ID_BTI
      || process.env.VUE_APP_SUPERSET_DASHBOARD_ID_BTE_V3
  },
}

const baseUrl = process.env.VUE_APP_API_BASE_URL

const mutations = {
  setEquipments(stateObj, equipments) {
    stateObj.equipments = equipments
  },
  updateEquipmentConnectionState(stateObj, updatedEquipments) {
    stateObj.equipments = stateObj.equipments.map(equipment => {
      const updatedEquipment = updatedEquipments.find(item => item.serialNumber === equipment.serialNumber)
      return updatedEquipment ? { ...equipment, ...updatedEquipment } : equipment
    })
  },
  updatedEquipmentCallHistory(stateObj, updatedEquipments) {
    stateObj.equipments = stateObj.equipments.map(equipment => {
      const updatedEquipment = updatedEquipments.find(item => item.serialNumber === equipment.serialNumber)
      return updatedEquipment ? { ...equipment, ...updatedEquipment } : equipment
    })
  },
}

function getEquipmentImagePaths(equipment, clientName) {
  // Extract the prefix from the serial number
  const prefix = equipment.serialNumber.match(/^[A-Za-z]+/)[0]
  const equipmentType = equipmentMap[prefix]
  const capitalizedEquipmentType = equipmentType.charAt(0).toUpperCase() + equipmentType.slice(1)

  return {
    imagePath: require(`@/assets/images/ht/equipments/${equipmentType}/${prefix}_${clientName}_small.png`),
    largeImagePath: require(`@/assets/images/ht/equipments/${equipmentType}/${capitalizedEquipmentType}_${clientName}.png`),
  }
}

const actions = {
  async fetchEquipments({ commit, dispatch, getters: localGetters }) {
    const userData = JSON.parse(localStorage.getItem('userData'))
    const token = useJwt.getToken()

    try {
      const response = await axios.get(`${baseUrl}/equipments?client=${userData.clientName}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          token: token,
        },
      })
      const equipments = response.data
        .filter(equipment => equipment.calling)
        .filter(equipment => equipment.companyName === userData.clientName)
        .map(equipment => {
          const clientGroup = localGetters.getClientGroup(userData.clientName)
          const { imagePath, largeImagePath } = getEquipmentImagePaths(equipment, clientGroup)
          return {
            serialNumber: equipment.serialNumber,
            name: equipment.name,
            status: false,
            imagePath: imagePath,
            largeImagePath: largeImagePath,
            latitude: equipment.latitude,
            longitude: equipment.longitude,
            connectionState: false,
            callHistory: [],
          }
        })
      commit('setEquipments', equipments)
      dispatch('fetchLiveStats', equipments)
      dispatch('fetchCallHistory', equipments)
    // eslint-disable-next-line no-empty
    } catch (error) {
    }
  },
  async fetchLiveStats({ commit, state: rootState }) {
    const equipments = rootState.equipments.map(equipment => equipment.serialNumber).join(',')
    const response = await axios.get(
      `${process.env.VUE_APP_FUNCTION_BASE_URL}/proxy/ping`,
      {
        params: {
          code: process.env.VUE_APP_FUNCTION_PROXY_KEY,
          equipments: equipments,
        },
      },
    )
    if (Array.isArray(response.data)) {
      const updatedEquipments = response.data.map(item => {
        const foundItem = rootState.equipments.find(equipment => equipment.serialNumber === item.serialNumber)
        if (foundItem) {
          return {
            ...foundItem,
            connectionState: item.connectionState === 'Connected',
          }
        }
        return foundItem
      })
      commit('updateEquipmentConnectionState', updatedEquipments)
    }
  },
  async fetchCallHistory({ commit, state: rootState }) {
    const serialNumbers = rootState.equipments.map(equipment => equipment.serialNumber).join(',')
    const token = useJwt.getToken()

    try {
      const response = await axios.get(`${baseUrl}/call/history?serialNumbers=${serialNumbers}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          token: token,
        },
      })
      const callHistory = response.data

      // Add call history to the equipment objects
      const equipmentsWithCallHistory = rootState.equipments.map(equipment => ({
        ...equipment,
        callHistory: callHistory[equipment.serialNumber] || [],
      }))

      commit('updatedEquipmentCallHistory', equipmentsWithCallHistory)
      // eslint-disable-next-line no-empty
    } catch (error) {
    }
  },
  // eslint-disable-next-line no-unused-vars
  async postCallHistory({ commit }, call) {
    const token = useJwt.getToken()

    try {
      await axios.post(`${baseUrl}/call/history`, call, {
        headers: {
          Authorization: `Bearer ${token}`,
          token: token,
        },
      })
    // eslint-disable-next-line no-empty
    } catch (error) {
    }
  },
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
}
